import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, UserRoundCheck } from "lucide-react";

// Friendly labels for the backend's missing_fields keys.
const FIELD_LABELS = {
  photo: "Profile photo",
  photo_url: "Profile photo",
  phone: "Phone number",
  occupation: "Occupation",
  income_range: "Income range",
  budget: "Monthly budget",
  budget_min: "Monthly budget",
  budget_max: "Monthly budget",
  move_in: "Move in date",
  lease_length_wanted: "Lease length",
  lease_length: "Lease length",
  about: "About you",
  name: "Full name",
};

function labelFor(key) {
  return FIELD_LABELS[key] || String(key).replaceAll("_", " ");
}

// Shown when a signed in user with an incomplete profile tries to swipe or
// publish. Guests never see this; they keep the existing signup gate.
export default function CompleteProfileGate({ open, onClose, missing = [], role = "renter" }) {
  const navigate = useNavigate();
  const dest = role === "lister" || role === "realtor" ? "/profile/lister" : "/profile";
  const items = [...new Set((missing || []).map(labelFor))];

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Finish your profile to start swiping"
            className="relative w-full max-w-sm rounded-2xl bg-white p-6 pt-8 text-center shadow-ambient"
          >
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="absolute right-3 top-3 rounded-full p-2 text-brand-secondary transition hover:bg-neutral-100 hover:text-brand-ink"
            >
              <X size={18} />
            </button>

            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-tint text-brand-lime">
              <UserRoundCheck size={22} strokeWidth={2} />
            </span>
            <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-brand-ink">
              Finish your profile to start swiping
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-brand-secondary">
              {role === "lister" || role === "realtor"
                ? "Complete your lister profile before publishing. It only takes a minute."
                : "Listers see your profile when you like their home, so it needs to be complete first."}
            </p>

            {items.length > 0 && (
              <div className="mt-4 rounded-2xl bg-neutral-50 p-4 text-left">
                {/* Amber dots, not checkmarks — these fields are MISSING, and a
                    check icon reads as already done. */}
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Still needed</p>
                <ul className="mt-2 space-y-1.5">
                  {items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm font-medium text-brand-ink">
                      <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(dest);
              }}
              className="mt-5 w-full rounded-full bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary-hover"
            >
              Complete my profile
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 w-full rounded-full px-4 py-2.5 text-sm font-semibold text-brand-secondary transition hover:text-brand-ink"
            >
              Not now
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
